import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type LightboxProps = {
  open: boolean;
  onClose: () => void;
  labelledBy?: string;
  ariaLabel?: string;
  maxWidth?: string;
  children: React.ReactNode;
};
export function Lightbox({
  open,
  onClose,
  labelledBy,
  ariaLabel,
  maxWidth = 'max-w-4xl',
  children
}: LightboxProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    window.setTimeout(() => panelRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [open, onClose]);
  return createPortal(<AnimatePresence data-id="element-327" data-mp="1zaurohue97b1">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} data-id="element-328" data-mp="1z9tarwqp5qle">
          <div aria-hidden className="absolute inset-0 bg-[#0A1A4D]/70 backdrop-blur-sm" onClick={onClose} data-id="element-329" data-mp="2n06wz6mlov82" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={labelledBy} aria-label={ariaLabel} tabIndex={-1} initial={{
        opacity: 0,
        scale: 0.94,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 10
      }} transition={{
        duration: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }} className={`relative flex max-h-[calc(100vh-2rem)] w-full ${maxWidth} flex-col overflow-hidden rounded-[28px] bg-white shadow-lg focus:outline-none sm:max-h-[calc(100vh-4rem)]`} data-id="element-330" data-mp="2i0ubap7gtu72">
            <button type="button" onClick={onClose} aria-label="Cerrar" className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-primary shadow-sm transition hover:bg-primary hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-331" data-mp="141910n4r5ziq">
              <XIcon className="h-5 w-5" data-id="element-332" data-mp="l3ojy288c58o" />
            </button>
            <div className="overflow-y-auto" data-id="element-333" data-mp="6ea7umd9r9pl">{children}</div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}